/**
 * Color control for color endpoints
 *
 * Two modes based on global palette setting:
 * - Free mode (no palette): Shows native color picker for any color
 * - Constrained mode (palette set): Shows palette swatches only
 *
 * Swatch is canvas-based to support live animation when getAnimatedValue is provided.
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { RGBA } from '@plugins/threejs/colorUtils';
import { rgbaToHex, hexToRgba } from '@plugins/threejs/colorUtils';
import { usePalette } from '@/ui/palettes/PaletteContext';
import { PALETTE_SLOTS, type PaletteSlot, getSlotColor } from '@/ui/palettes/paletteTypes';
import { ColorPicker } from './ColorPicker';
import { useRafLoop } from '@/ui/hooks';

interface ColorControlProps {
  value: RGBA;
  onChange: (value: RGBA) => void;
  getAnimatedValue?: () => RGBA;
  options?: (RGBA | string)[];
  optionLabels?: string[];
}

function renderSwatchCanvas(canvas: HTMLCanvasElement, rgba: RGBA) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = `rgba(${Math.round(rgba.r * 255)},${Math.round(rgba.g * 255)},${Math.round(rgba.b * 255)},${rgba.a})`;
  ctx.fillRect(0, 0, 24, 24);
}

export function ColorControl({ value, onChange, getAnimatedValue, options, optionLabels }: ColorControlProps) {
  const { palette, isConstrained, savedColors, saveColor } = usePalette();
  const propHex = value ? rgbaToHex(value) : '#000000';
  const alpha = value?.a ?? 1;

  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Animated: RAF loop when getAnimatedValue is provided
  useRafLoop(() => {
    if (!getAnimatedValue) return false; // stop loop
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderSwatchCanvas(canvas, getAnimatedValue());
  }, [getAnimatedValue]);

  // Static: update canvas on value change when not animated
  useEffect(() => {
    if (getAnimatedValue) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderSwatchCanvas(canvas, value ?? { r: 0, g: 0, b: 0, a: 1 });
  }, [value, getAnimatedValue]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedSwatch = canvasRef.current?.contains(target);
      const clickedPopover = popoverRef.current?.contains(target);
      if (!clickedSwatch && !clickedPopover) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSwatchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      // Position popover - different widths for free vs constrained mode
      const popoverWidth = isConstrained ? 140 : 220;
      setPopoverPos({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - popoverWidth)
      });
    }
    setIsOpen(!isOpen);
  };

  // Handle slot selection in constrained mode
  const handleSlotSelect = (slot: PaletteSlot) => {
    if (!palette) return;
    const hex = getSlotColor(palette, slot);
    const newColor = hexToRgba(hex, alpha);
    onChange(newColor);
    setIsOpen(false);
  };

  // Handle free color change from picker
  const handleFreeColorChange = (hex: string) => {
    const newColor = hexToRgba(hex, alpha);
    onChange(newColor);
  };

  // Find which slot matches current color (if any) in constrained mode
  const currentSlot = palette ? PALETTE_SLOTS.find(
    slot => getSlotColor(palette, slot).toLowerCase() === propHex.toLowerCase()
  ) : undefined;

  const swatchCanvas = (
    <canvas
      ref={canvasRef}
      width={24}
      height={24}
      onClick={handleSwatchClick}
      className="nodrag"
      style={{
        width: 24,
        height: 24,
        border: '1px solid #444',
        borderRadius: 3,
        cursor: 'pointer'
      }}
      title={isConstrained ? (currentSlot || propHex) : propHex}
    />
  );

  // Options mode — show inline swatches
  if (options?.length) {
    const currentHex = propHex.toLowerCase();
    return (
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}>
        {options.map((opt, i) => {
          const isHex = typeof opt === 'string';
          const hex = isHex ? opt : rgbaToHex(opt);
          const rgba = isHex ? hexToRgba(opt) : opt;
          const selected = hex.toLowerCase() === currentHex;
          return (
            <div
              key={i}
              onClick={(e) => { e.stopPropagation(); onChange(rgba); }}
              className="nodrag nopan"
              style={{
                width: 22,
                height: 22,
                backgroundColor: hex,
                border: selected ? '2px solid #fff' : '1px solid #444',
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'transform 0.1s',
              }}
              title={optionLabels?.[i] ?? hex}
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.15)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            />
          );
        })}
      </div>
    );
  }

  // Free color mode - show custom color picker
  if (!isConstrained) {
    return (
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}
      >
        {swatchCanvas}

        {/* Free color picker popover */}
        {isOpen && createPortal(
          <div
            ref={popoverRef}
            className="nodrag"
            data-endpoint-portal
            style={{
              position: 'fixed',
              top: popoverPos.top,
              left: popoverPos.left,
              padding: 10,
              background: '#1f2937',
              border: '1px solid #374151',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 10000
            }}
          >
            <ColorPicker
              value={propHex}
              onChange={handleFreeColorChange}
              savedColors={savedColors}
              onSaveColor={saveColor}
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Constrained mode - show palette swatches
  return (
    <div
      style={{
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 4
      }}
    >
      {swatchCanvas}

      {/* Palette swatch picker popover - rendered via portal to escape overflow:hidden */}
      {isOpen && palette && createPortal(
        <div
          ref={popoverRef}
          className="nodrag"
          data-endpoint-portal
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            padding: 8,
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 10000,
            minWidth: 140
          }}
        >
          <div style={{
            fontSize: 10,
            color: '#9ca3af',
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {palette.name}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 4
          }}>
            {PALETTE_SLOTS.map(slot => (
              <div
                key={slot}
                onClick={() => handleSlotSelect(slot)}
                style={{
                  width: 28,
                  height: 28,
                  backgroundColor: getSlotColor(palette, slot),
                  border: currentSlot === slot ? '2px solid #fff' : '1px solid #444',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'transform 0.1s'
                }}
                title={slot}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
